/**
 * PharmacyLinkAction.tsx — ADMIN: a fresh pharmacy sign-in link for a paid
 * order, with the clock on it (HOTFIX 2026-09-11, extended after the live
 * incident the same day).
 *
 * WHY: the dispensing console signs in with a single-use link that expires.
 * The one in the dispense email — or copied into the pharmacy's handbook — is
 * routinely dead by the time the pharmacy opens it, and the sign-in page can
 * only tell them to "ask the GP Service team to send a new one". There was no
 * way for the team to do that, so a paid order could sit undispensed.
 *
 * WHY IT GREW A COUNTDOWN (INCIDENT, 2026-09-11). Pharmacy Express could not
 * get in and two real patients waited. The panel showed an absolute clock time
 * ("Works once, until 20:30.") on a link held in component state that never
 * re-rendered, so a link minted ninety minutes earlier still read as good. An
 * admin taking the phone call could not tell whether the link the pharmacy was
 * holding was worth trying. Worse, every failed retry counts against the
 * pharmacist's own lockout budget, so "just try it again" is not free — it is
 * how a fifteen-minute cooldown became a multi-hour outage. The panel now
 * answers the phone-call question at a glance: TIME REMAINING while it is
 * good, and an explicit Expired state with a new link one click away once it
 * is not.
 *
 * ⚠️ THE LINK IS A CREDENTIAL.
 *   - It is shown only to an ADMIN. `@Roles('ADMIN')` on
 *     POST /v1/orders/:id/pharmacy-link is the real control; hiding it here is
 *     affordance, not security.
 *   - It is NEVER logged, NEVER put in a URL or query string, and NEVER handed
 *     to analytics. There is deliberately no `onCopied` hook on the copy button
 *     (the plan link has one, for a contact-attempt audit) — orders-service
 *     already writes `pharmacy_signin_link_issued` before the link is returned,
 *     and a second record of a credential is a second place to leak it.
 *   - It is not sufficient on its own: the pharmacist must also enter their
 *     pharmacist code, which is why sending it over WhatsApp to the pharmacy
 *     team is acceptable.
 *
 * ⚠️ IT WORKS ONCE, AND THE COPY SAYS SO. The first browser to open the link
 * spends it, for everyone. That property caused the outage — an admin opened
 * the link to check it, which burned it for the pharmacy — so it is on the
 * panel in plain words rather than in this comment alone.
 *
 * "Pharmacist code" is the ONE name for that code everywhere a pharmacy or an
 * admin reads it — the sign-in page, the dispense dialog and the dispense email
 * all say it — so this copy must not invent a second one.
 *
 * No confirm dialog, unlike the payment link beside it: this contacts nobody
 * and moves no money. It only hands the admin something to send.
 *
 * ONCE A LINK IS OUT, THE PANEL IS ONE ROW (CTO, 2026-09-25, on a closed/won
 * deal: "after creating the link I think we're using too much space here …
 * we can show two small buttons once the link is generated; till then the CTA
 * can have a button there"). A full-width "Create another pharmacy link" above
 * the URL and a full-width "Copy pharmacy sign-in link" below it made the
 * panel five stacked blocks. Now:
 *  - BEFORE a link, unchanged: the one "Get a pharmacy sign-in link" button.
 *  - WITH a live (or unknown-expiry) link: the validity chip, then the URL box
 *    with two COMPACT buttons on its right — Copy and Create another — and the
 *    safety paragraph under them in small muted type, WORDING UNCHANGED (it is
 *    the single-use / expiry instruction the incident was about).
 *  - EXPIRED, unchanged: the badge, the warning and the one primary "Generate
 *    new link". Still exactly ONE mint control on screen in every state.
 * The two buttons sit in the SAME row as the URL box and wrap under it, side
 * by side, when the row is narrower than ~560px (the URL box's 300px basis +
 * the buttons). The wrap is decided by the ROW's width, not the viewport's: the
 * drawer is two thirds of the window, and the admin console's floor is 768px.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { ApiError } from '../api/client';
import { issuePharmacySignInLink, type PharmacySignInLinkResult } from '../api/orders';
import { CompactActionLabel, compactActionStyle, CopyLinkButton } from '../staff/ui';
import { COUNTDOWN_TICK_MS, lifetimeAt, sameLifetime, type PharmacyLinkLifetime } from './pharmacyLinkLifetime';
import { Alert, Badge, Button, Help } from '../ui/kit';
import { IconCopyPlanLink, IconRefresh } from '../ui/icons';

/**
 * The words every control in the minted row adds to its name, visually hidden
 * ("Copy" → "Copy pharmacy sign-in link"). The drawer can also carry a Copy
 * for the PAYMENT link, so a bare "Copy" would be ambiguous to a screen reader
 * and to voice control.
 */
const LINK_NOUN = 'pharmacy sign-in link';

/**
 * The URL box's flex basis. With the two compact buttons (104px + 144px and a
 * 6px gap, both held at those widths below) it puts the wrap point at a row of
 * roughly 560px — below that the buttons drop under the box, side by side,
 * rather than squeezing a credential into a sliver of monospace.
 */
const URL_BOX_BASIS = '300px';

/**
 * EACH BUTTON IS HELD AT THE WIDTH OF ITS WIDEST WORDS, IN EVERY STATE.
 *
 * The URL box takes whatever the buttons leave, so a button that changes width
 * re-wraps the link, and at a row near the wrap point it moves the pair between
 * "beside the box" and "under it". Both buttons change their words: Copy says
 * "Copied" with a tick, and Create another drops its glyph and says "Creating…"
 * while it mints. Measured in Chrome with the bundled DM Sans 600 at 13px
 * (release-2026-09-25/pharmacy-link-compact/metrics.json):
 *  - Copy: its widest state is "Copied" with the tick, 91.3px ("Copy" with its
 *    glyph is 80.9px). 6.5rem (104px) holds it.
 *  - Create another: its widest state is the idle "Create another" with the
 *    glyph, 142.9px. Unheld it fell to 88.4px while busy. At the 900px console
 *    (a 540px row) that moved the pair from under the URL to beside it and took
 *    the link from 2 lines to 3, then back when the mint settled. At the 1024px
 *    console it took the link from 3 lines to 2 and back. 9rem (144px) holds it.
 * This matters because the OLD link stays on screen during the mint on purpose,
 * so the admin can keep reading it down the phone. A link that re-flows while
 * someone reads it out is what the hold prevents. The hold also keeps the button
 * under the pointer that pressed it.
 *
 * Both are `rem`, matching the 6.5rem Copy shipped with. The labels are a fixed
 * 13px, so a larger browser default font only widens the floor. Re-measure both
 * if either label or the type changes (`pharmacyLink.spec.tsx` §6 carries the
 * measured widths).
 */
const COPY_MIN_WIDTH = '6.5rem';
const CREATE_ANOTHER_MIN_WIDTH = '9rem';

/**
 * The live countdown for one minted link.
 *
 * TAKES THE WHOLE MINTED RESULT, NOT ITS `expiresAt` STRING. Regenerating is
 * the main thing this panel does, and two mints inside the same minute can come
 * back with the same expiry string. Keying the effect on the object identity —
 * which changes on every successful mint — means a new link always restarts the
 * clock, including in that case.
 *
 * DEGRADES TO A STATIC VALUE. If no timer can be started (a hardened embedding,
 * a stubbed environment) the reading taken at mint time stands and the panel
 * still renders. A frozen-but-true "3h 16m" is worth far more to somebody on
 * the phone than a blank space or a thrown error.
 *
 * A NEW LINK IS READ IN THE RENDER THAT FIRST DRAWS IT (2026-09-29). The reading
 * used to be taken only in the effect below, which runs AFTER the frame is
 * committed, so every mint painted one frame of the good link beside "Expiry
 * unknown" and "We cannot tell when this one stops working". That frame is what
 * failed the prod portal deploy twice: on a slow runner the assertions landed in
 * it. Setting state during render when the link changes is React's own pattern
 * for this ("storing information from previous renders"): React re-runs the
 * component before committing, so the stale frame is never painted. The value
 * returned in that same render is the fresh one too, so no render with the new
 * link ever hands back the old reading. `pharmacyLink.spec.tsx` §2 records every
 * committed frame to prove it.
 */
function useLinkLifetime(link: { expiresAt: string | null } | null): PharmacyLinkLifetime {
  const [lifetime, setLifetime] = useState<PharmacyLinkLifetime>(() => lifetimeAt(link?.expiresAt, Date.now()));
  const [readFor, setReadFor] = useState(link);

  let current = lifetime;
  if (readFor !== link) {
    current = lifetimeAt(link?.expiresAt, Date.now());
    setReadFor(link);
    setLifetime(current);
  }

  useEffect(() => {
    // Re-read before any tick: a freshly minted link must never show the dead
    // one's remaining time, not even for a second. The render above has already
    // read it; this keeps the same object when nothing a reader can see moved.
    const initial = lifetimeAt(link?.expiresAt, Date.now());
    setLifetime((prev) => (sameLifetime(prev, initial) ? prev : initial));

    // Nothing to count down: no link, no readable expiry, or already lapsed.
    if (initial.state !== 'live') return;

    // THE TRY IS THE WHOLE DEGRADE PATH. Anything that stops a timer being
    // started — no `setInterval` at all, or one that refuses — throws here, and
    // the static reading taken above is what the admin gets. There is
    // deliberately no separate `typeof` probe: two ways to express one
    // condition means one of them is never exercised.
    let timer: number | undefined;
    try {
      timer = window.setInterval(() => {
        const next = lifetimeAt(link?.expiresAt, Date.now());
        setLifetime((prev) => (sameLifetime(prev, next) ? prev : next));
        // Once it has lapsed there is nothing left to count. `clearInterval`
        // takes an optional id, so no undefined-check is needed to satisfy it.
        if (next.state !== 'live') window.clearInterval(timer);
      }, COUNTDOWN_TICK_MS);
    } catch {
      return;
    }
    return () => window.clearInterval(timer);
  }, [link]);

  return current;
}

export function PharmacyLinkAction({ orderCaseId }: { orderCaseId: string }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<PharmacySignInLinkResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * THE DOUBLE-SUBMIT GUARD, AND WHY IT IS A REF.
   *
   * `busy` is React state: two clicks dispatched before React commits both read
   * it as `false`, and a disabled attribute that has not rendered yet disables
   * nothing. A ref is written synchronously inside the first handler, so the
   * second returns immediately.
   *
   * This is not cosmetic. Each mint issues a DIFFERENT single-use token, so
   * double-minting leaves the admin holding link B while the pharmacy was read
   * link A — and the pharmacy's failed attempt at the stale one counts against
   * the pharmacist's lockout budget. Minting twice is a real way to make an
   * outage worse, so it is stopped here as well as being disabled below.
   */
  const inFlight = useRef(false);

  /**
   * KEEP THE KEYBOARD ON "Create another" ACROSS ITS OWN MINT.
   *
   * The button is `disabled` while the mint runs (the same busy state as every
   * mint control here), and a browser moves focus off a control that becomes
   * disabled — to `<body>`, so a keyboard user who pressed it would have to Tab
   * back from the top of the page to reach the new link (WCAG 2.4.3). When the
   * press came from that button, focus goes back to it once the mint settles,
   * success or failure — but only if focus has not gone somewhere else in the
   * meantime. The kit `Button` takes no ref, so it is found by its id.
   */
  const createAnotherId = useId();
  const refocusCreateAnother = useRef(false);

  const minted = result?.outcome === 'minted' ? result : null;
  const lifetime = useLinkLifetime(minted);
  const expired = minted !== null && lifetime.state === 'expired';

  useEffect(() => {
    if (busy || !refocusCreateAnother.current) return;
    refocusCreateAnother.current = false;
    const button = document.getElementById(createAnotherId);
    const active = document.activeElement;
    if (button && (active === null || active === document.body || active === button)) button.focus();
  }, [busy, createAnotherId]);

  function mintAnother() {
    refocusCreateAnother.current = document.activeElement === document.getElementById(createAnotherId);
    void mint();
  }

  async function mint() {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      setResult(await issuePharmacySignInLink(orderCaseId));
    } catch (err) {
      setError(
        err instanceof ApiError && err.detail
          ? `We couldn't create the link — ${err.detail}`
          : 'Sorry — we could not create a pharmacy sign-in link. Please try again.',
      );
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: '6px', justifyItems: 'start' }}>
      {/* ONE mint control on screen at a time. Before any link this is the
          quiet ghost "Get a pharmacy sign-in link" — the CTA the CTO kept. Once
          a link is out, "Create another" moves into the link's own row (below),
          and once it lapses the Expired panel carries the one primary button.
          `!minted` also covers the stub-environment answer, which minted
          nothing and so leaves the first CTA where it was. */}
      {!minted ? (
        <Button type="button" variant="ghost" onClick={() => void mint()} disabled={busy} style={{ width: 'auto' }}>
          {busy ? 'Creating…' : 'Get a pharmacy sign-in link'}
        </Button>
      ) : null}

      {error ? (
        <Alert tone="danger" role="alert" style={{ marginBottom: 0 }}>
          {error}
        </Alert>
      ) : null}

      {minted && expired ? (
        <>
          <Badge tone="danger">Expired</Badge>
          <Alert tone="warning" role="status" style={{ marginBottom: 0 }}>
            This link has run out and will not let the pharmacy in. Generate a new one and send them that — asking
            them to retry the old one counts against their sign-in attempts and can lock their pharmacist code.
          </Alert>
          <Button type="button" variant="primary" onClick={() => void mint()} disabled={busy} style={{ width: 'auto' }}>
            {busy ? 'Creating…' : 'Generate new link'}
          </Button>
        </>
      ) : null}

      {minted && !expired ? (
        <>
          {/* The remaining time is the answer to the question an admin is being
              asked on the phone, so it leads. Not a live region: it changes on
              its own once a minute and announcing that would talk over the
              caller. The words carry the state — the pill's colour never does. */}
          {lifetime.state === 'live' ? (
            <Badge tone="positive">{`Valid for ${lifetime.label}`}</Badge>
          ) : (
            <Badge tone="pending">Expiry unknown</Badge>
          )}

          {/* THE LINK AND ITS TWO ACTIONS, ONE ROW (CTO, 2026-09-25). The URL
              box grows into the row and the buttons keep their own width on its
              right; a row narrower than the box's basis plus the buttons wraps
              the buttons UNDER the box, still side by side. `justifySelf:
              stretch` because the panel's grid starts every other child at its
              content width, and this row has to know how wide the band is. */}
          <div
            style={{
              justifySelf: 'stretch',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            {/* THE LINK ITSELF, AS TEXT. Copy is the normal path, but an admin on
                a call needs to be able to read it back, or check that what the
                pharmacy is quoting is the same link. Selectable, wrapping, and
                never truncated — a truncated credential read down a phone is
                worse than none. `minWidth: 0` lets it wrap inside the row
                instead of pushing the buttons off the edge. */}
            <code
              style={{
                flex: `1 1 ${URL_BOX_BASIS}`,
                minWidth: 0,
                font: 'inherit',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: '12px',
                lineHeight: 1.5,
                wordBreak: 'break-all',
                background: 'var(--gps-surface-2)',
                color: 'var(--gps-text)',
                border: '1px solid var(--gps-border)',
                borderRadius: 'var(--gps-r-sm)',
                padding: '8px 10px',
              }}
            >
              {minted.authUrl}
            </code>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', flex: '0 0 auto' }}>
              {/* A real <button>, so it is reachable and operable from the
                  keyboard, and it flashes "Copied" with a tick when the value
                  actually reached the clipboard — never before. The name is
                  "Copy pharmacy sign-in link" (visible "Copy", the rest visually
                  hidden). No `onCopied`: see the credential note at the top of
                  this file — orders-service already audits the ISSUE, and a
                  second record of a credential is a second place to leak it. */}
              <CopyLinkButton
                value={minted.authUrl}
                label="Copy"
                hiddenLabel={LINK_NOUN}
                variant="compact"
                icon={<IconCopyPlanLink size={16} />}
                /* Holds the width of "Copied", so the tick does not nudge the
                   URL box and re-wrap the link under the admin's eyes. */
                style={{ minWidth: COPY_MIN_WIDTH }}
              />
              {/* THE SAME mint as every other control here: the double-submit
                  ref, "Creating…" while it runs, the server's own sentence on
                  a refusal. A new link REPLACES this one on screen (the clock
                  restarts on the new result); no confirm, as before — it
                  contacts nobody and moves no money. The glyph is dropped while
                  busy: a shape holding still under changing words reads as a
                  stuck control. The WIDTH is not dropped: it is held at the
                  idle width in both states, so the old link, still on screen
                  while the new one is minted, does not re-wrap (see
                  CREATE_ANOTHER_MIN_WIDTH). */}
              <Button
                type="button"
                variant="ghost"
                id={createAnotherId}
                onClick={mintAnother}
                disabled={busy}
                style={{ ...compactActionStyle, minWidth: CREATE_ANOTHER_MIN_WIDTH }}
              >
                {busy ? null : <IconRefresh size={16} />}
                <CompactActionLabel shown={busy ? 'Creating…' : 'Create another'} hidden={LINK_NOUN} />
              </Button>
            </div>
          </div>

          {/* The single-use and expiry instruction, WORD FOR WORD what it was —
              this is the sentence the 2026-09-11 outage was about. Only its
              size changed: small muted type, so it reads as the note under the
              row rather than a paragraph competing with it. `--gps-muted` is
              5.01:1 on the neutral and green band grounds and 4.77:1 on the
              amber one, so it holds AA at any size. */}
          <Help style={{ margin: 0, fontSize: '12.5px', lineHeight: 1.45 }}>
            {lifetime.state === 'live'
              ? `Works once, and stops working at ${lifetime.until}.`
              : 'Works once. We cannot tell when this one stops working, so it may already have — if the pharmacy cannot get in, create another.'}{' '}
            Do not open it yourself: the first browser to open it uses it up and the pharmacy is locked out. Send it
            to the pharmacy team — they will also need their pharmacist code to sign in.
          </Help>
        </>
      ) : null}

      {result?.outcome === 'stub' ? (
        <Alert tone="warning" role="status" style={{ marginBottom: 0 }}>
          Pharmacy sign-in links are not switched on in this environment, so no link was created.
        </Alert>
      ) : null}
    </div>
  );
}
